import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from "@/components/ui/card";
import { Copy, Users, DollarSign, TrendingUp, Wallet, Settings } from 'lucide-react';
import { toastSuccess, toastError } from "@/components/ui/use-toast";
import { useTranslation } from '@/hooks/useTranslation';
import { useMobile } from '@/hooks/useResponsiveDesign';
import { API_BASE_URL } from '../config';

interface ReferralProfile {
  id: string;
  referral_code: string;
  is_active: boolean;
  solana_wallet_address: string | null;
  auto_payment_enabled: boolean;
  minimum_payout: number;
  total_referrals: number;
  active_referrals: number;
  total_earnings: number;
  total_paid: number;
  pending_payout: number;
  eligible_for_payout: boolean;
}

interface ReferralStats {
  period: string;
  statistics: {
    total_referrals: number;
    verified_referrals: number;
    active_referrals: number;
    verification_rate: number;
    total_earnings: number;
    paid_earnings: number;
    pending_earnings: number;
    current_tier: number;
    earning_rate: number;
    average_earning_per_referral: number;
  };
}

export default function ReferralDashboard() {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const [profile, setProfile] = useState<ReferralProfile | null>(null);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [earnings, setEarnings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [referralLink, setReferralLink] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [minPayout, setMinPayout] = useState(10);
  const [autoPayment, setAutoPayment] = useState(true);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch profile
      const profileResponse = await fetch(`${API_BASE_URL}/api/referrals/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setProfile(profileData.profile);
        setReferrals(profileData.referrals);
        setReferralLink(profileData.referral_link);
        setWalletAddress(profileData.profile.solana_wallet_address || '');
        setMinPayout(Math.max(profileData.profile.minimum_payout || 10, 10));
        setAutoPayment(profileData.profile.auto_payment_enabled);
      }

      // Fetch statistics
      const statsResponse = await fetch(`${API_BASE_URL}/api/referrals/statistics?period=30d`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      // Fetch recent earnings
      const earningsResponse = await fetch(`${API_BASE_URL}/api/referrals/earnings?per_page=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (earningsResponse.ok) {
        const earningsData = await earningsResponse.json();
        setEarnings(earningsData.earnings);
      }

    } catch (error) {
      console.error('Error fetching referral data:', error);
      toastError({
        title: t('common.error'),
        description: t('referrals.errors.failedToLoadData')
      });
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toastSuccess({
      title: t('referrals.messages.copied'),
      description: t('referrals.messages.linkCopied'),
    });
  };

  const generateNewCode = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/referrals/code/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReferralLink(data.referral_link);
        toastSuccess({
          title: t('common.success'),
          description: t('referrals.messages.newCodeGenerated'),
        });
      }
    } catch (error) {
      toastError({
        title: t('common.error'),
        description: t('referrals.errors.failedToGenerateCode')
      });
    }
  };

  const updateProfile = async () => {
    // Validate minimum payout
    if (minPayout < 10) {
      toastError({
        title: t('referrals.errors.invalidMinimumPayout'),
        description: t('referrals.errors.minimumPayoutRequired')
      });
      return;
    }

    // Validate wallet address format (basic Solana address validation)
    if (walletAddress && (walletAddress.length < 32 || walletAddress.length > 44)) {
      toastError({
        title: t('referrals.errors.invalidWalletAddress'),
        description: t('referrals.errors.validWalletRequired')
      });
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/referrals/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          solana_wallet_address: walletAddress,
          minimum_payout: Math.max(minPayout, 10), // Ensure minimum is 10
          auto_payment_enabled: autoPayment
        })
      });

      if (response.ok) {
        toastSuccess({
          title: t('common.success'),
          description: t('referrals.messages.profileUpdated'),
        });
        setShowSettings(false);
        fetchReferralData();
      }
    } catch (error) {
      toastError({
        title: t('common.error'),
        description: t('referrals.errors.failedToUpdateProfile')
      });
    }
  };

  const requestPayout = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/referrals/payout/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toastSuccess({
          title: t('common.success'),
          description: t('referrals.messages.payoutRequestSubmitted'),
        });
        fetchReferralData();
      } else {
        const error = await response.json();
        toastError({
          title: t('common.error'),
          description: error.error || t('referrals.errors.failedToRequestPayout')
        });
      }
    } catch (error) {
      toastError({
        title: t('common.error'),
        description: t('referrals.errors.failedToRequestPayout')
      });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">{t('referrals.loading')}</div>
      </div>
    );
  }

  const tierRates = {
    1: { rate: 0.5, name: t('referrals.tierNames.tier1') },
    2: { rate: 1.0, name: t('referrals.tierNames.tier2') },
    3: { rate: 2.0, name: t('referrals.tierNames.tier3') }
  };

  const currentTier = stats?.statistics.current_tier || 1;
  const currentRate = tierRates[currentTier as keyof typeof tierRates];

  // Note: Removed mobile redirect to ensure feature parity between desktop and mobile

  return (
    <div className={`${isMobile ? 'p-3 space-y-4' : 'p-6 space-y-6'} max-w-full overflow-x-hidden`}>
      <div className={`flex ${isMobile ? 'flex-col gap-3' : 'justify-between items-center'}`}>
        <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>{t('referrals.title')}</h1>
        <Button
          variant="outline"
          size={isMobile ? "sm" : "sm"}
          onClick={() => setShowSettings(!showSettings)}
          className={isMobile ? 'self-start' : ''}
        >
          <Settings className="w-4 h-4 mr-2" />
          {t('referrals.settings.title')}
        </Button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card>
          <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
            <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold ${isMobile ? 'mb-3' : 'mb-4'}`}>{t('referrals.settings.referralSettings')}</h3>
            <div className={`${isMobile ? 'space-y-3' : 'space-y-4'}`}>
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium ${isMobile ? 'mb-1' : 'mb-2'}`}>{t('referrals.settings.solanaWalletAddress')}</label>
                <input
                  type="text"
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  placeholder={t('referrals.settings.walletPlaceholder')}
                  className={`w-full ${isMobile ? 'p-2 text-sm' : 'p-2'} border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400`}
                />
                <div className={`mt-2 ${isMobile ? 'p-2' : 'p-3'} bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md`}>
                  <div className="flex items-start gap-2">
                    <span className={`text-red-600 dark:text-red-400 ${isMobile ? 'text-xs' : 'text-sm'} font-bold mt-0.5`}>⚠️</span>
                    <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-red-700 dark:text-red-300`}>
                      <p className="font-semibold mb-1">{t('referrals.settings.importantWarning')}</p>
                      <ul className="space-y-1">
                        <li>• {t('referrals.settings.warnings.ensureCorrectAddress')}</li>
                        <li>• {t('referrals.settings.warnings.doubleCheckAddress')}</li>
                        <li>• {t('referrals.settings.warnings.notResponsible')}</li>
                        <li>• {t('referrals.settings.warnings.cannotRecover')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium ${isMobile ? 'mb-1' : 'mb-2'}`}>{t('referrals.settings.minimumPayoutAmount')}</label>
                <input
                  type="number"
                  value={minPayout}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value >= 10) {
                      setMinPayout(value);
                    }
                  }}
                  min="10"
                  max="1000"
                  className={`w-full ${isMobile ? 'p-2 text-sm' : 'p-2'} border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                />
                <div className={`mt-2 ${isMobile ? 'p-2' : 'p-3'} bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md`}>
                  <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-700 dark:text-blue-300`}>
                    <p className="font-semibold mb-1">{t('referrals.settings.minimumPayoutRequirements')}</p>
                    <ul className="space-y-1">
                      <li>• {t('referrals.settings.requirements.minimumAmount')}</li>
                      <li>• {t('referrals.settings.requirements.gasFeeDeducted')}</li>
                      <li>• {t('referrals.settings.requirements.estimatedGasFee')}</li>
                      <li>• {t('referrals.settings.requirements.payoutsProcessed')}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={autoPayment}
                    onChange={(e) => setAutoPayment(e.target.checked)}
                    id="auto-payment"
                  />
                  <label htmlFor="auto-payment" className={`${isMobile ? 'text-xs' : 'text-sm'}`}>{t('referrals.settings.enableAutomaticPayments')}</label>
                </div>
                <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground ${isMobile ? 'ml-4' : 'ml-6'}`}>
                  {autoPayment
                    ? t('referrals.settings.autoPaymentEnabled')
                    : t('referrals.settings.autoPaymentDisabled')}
                </p>
              </div>
              <div className={`flex ${isMobile ? 'flex-col gap-2' : 'gap-2'}`}>
                <Button onClick={updateProfile} size={isMobile ? "sm" : "default"} className={isMobile ? 'text-sm' : ''}>{t('referrals.settings.saveSettings')}</Button>
                <Button variant="outline" onClick={() => setShowSettings(false)} size={isMobile ? "sm" : "default"} className={isMobile ? 'text-sm' : ''}>{t('common.cancel')}</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overview Cards */}
      <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-4 gap-4'}`}>
        <Card>
          <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
            <div className="flex items-center gap-2">
              <Users className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-blue-500`} />
              <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-muted-foreground`}>{t('referrals.overview.totalReferrals')}</h3>
            </div>
            <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>{profile?.total_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">
              {t('referrals.overview.activeCount', { count: profile?.active_referrals || 0 })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
            <div className="flex items-center gap-2">
              <DollarSign className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-green-500`} />
              <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-muted-foreground`}>{t('referrals.overview.totalEarnings')}</h3>
            </div>
            <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>${profile?.total_earnings.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-muted-foreground">
              {t('referrals.overview.totalPaid', { amount: profile?.total_paid.toFixed(2) || '0.00' })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
            <div className="flex items-center gap-2">
              <Wallet className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-orange-500`} />
              <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-muted-foreground`}>{t('referrals.overview.pendingPayout')}</h3>
            </div>
            <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>${profile?.pending_payout.toFixed(2) || '0.00'}</p>
            {profile?.eligible_for_payout && (
              <Button size="sm" className={`${isMobile ? 'mt-2 text-xs' : 'mt-2'}`} onClick={requestPayout}>
                {t('referrals.overview.requestPayout')}
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
            <div className="flex items-center gap-2">
              <TrendingUp className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-purple-500`} />
              <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-muted-foreground`}>{t('referrals.overview.earningRate')}</h3>
            </div>
            <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>{currentRate.rate}%</p>
            <p className="text-xs text-muted-foreground">
              {currentRate.name}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link */}
      <Card>
        <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
          <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold ${isMobile ? 'mb-3' : 'mb-4'}`}>{t('referrals.referralLink')}</h3>
          <div className={`${isMobile ? 'flex flex-col gap-2' : 'flex gap-2'}`}>
            <input
              type="text"
              value={referralLink}
              readOnly
              className={`${isMobile ? 'w-full' : 'flex-1'} p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${isMobile ? 'text-sm' : ''}`}
            />
            <div className={`${isMobile ? 'flex gap-2' : 'contents'}`}>
              <Button onClick={copyReferralLink} size={isMobile ? "sm" : "default"} className={`${isMobile ? 'flex-1 text-xs' : ''}`}>
                <Copy className={`${isMobile ? 'w-3 h-3 mr-1' : 'w-4 h-4 mr-2'}`} />
                {t('referrals.actions.copy')}
              </Button>
              <Button variant="outline" onClick={generateNewCode} size={isMobile ? "sm" : "default"} className={`${isMobile ? 'flex-1 text-xs' : ''}`}>
                {t('referrals.actions.generateNew')}
              </Button>
            </div>
          </div>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground mt-2`}>
            {t('referrals.referralLinkDescription', { rate: currentRate.rate })}
          </p>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'lg:grid-cols-2 gap-6'}`}>
        {/* Recent Referrals */}
        <Card>
          <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
            <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold ${isMobile ? 'mb-3' : 'mb-4'}`}>{t('referrals.recentActivity.recentReferrals')}</h3>
            {referrals.length > 0 ? (
              <div className="space-y-3">
                {referrals.slice(0, 5).map((referral) => (
                  <div key={referral.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{referral.referee_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(referral.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs ${
                        referral.is_verified 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {referral.is_verified ? t('referrals.status.verified') : t('referrals.status.pending')}
                      </span>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('referrals.recentActivity.earned', { amount: referral.total_earnings.toFixed(2) })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">{t('referrals.recentActivity.noReferrals')}</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Earnings */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">{t('referrals.recentActivity.recentEarnings')}</h3>
            {earnings.length > 0 ? (
              <div className="space-y-3">
                {earnings.slice(0, 5).map((earning) => (
                  <div key={earning.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">${earning.amount.toFixed(2)}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(earning.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs ${
                        earning.is_paid 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {earning.is_paid ? t('referrals.status.paid') : t('referrals.status.pending')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">{t('referrals.recentActivity.noEarnings')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
